import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, Check, FileText, ShieldCheck, Video, CreditCard, User, Phone, HelpCircle,
  CircleAlert, Loader2, PlayCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Document catalog — order matches backend REQUIRED_DRIVER_DOCS. Icons + copy
// give the driver a scannable checklist so they know what to have ready.
const DOC_CATALOG = [
  { key: "drivers_license",       label: "Driver's License",                 hint: "Front and back, clearly legible" },
  { key: "national_id",           label: "National ID or Passport",          hint: "Government-issued, unexpired" },
  { key: "police_certificate",    label: "Police Certificate of Character",  hint: "Issued within the last 6 months" },
  { key: "proof_of_address",      label: "Proof of Address",                 hint: "Utility bill or bank letter" },
  { key: "vehicle_registration",  label: "Vehicle Registration",             hint: "Matches your driving vehicle" },
  { key: "insurance_certificate", label: "Insurance Certificate",            hint: "Valid comprehensive cover" },
  { key: "vehicle_inspection",    label: "Vehicle Inspection",               hint: "Current inspection sticker or cert" },
  { key: "profile_photo",         label: "Profile Photo",                    hint: "Clear head-and-shoulders shot" },
  { key: "selfie_verification",   label: "Selfie Verification",              hint: "Hold your ID next to your face" },
];

const ACCEPT_MIME = "image/*,application/pdf";

export default function DriverOnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [config, setConfig] = useState({ training_video_url: "", required_documents: [] });
  const [docs, setDocs] = useState({}); // { doc_key: { storage_path, size } }
  const [uploading, setUploading] = useState({}); // { doc_key: true/false }
  const [emergency, setEmergency] = useState({ name: "", phone: "", relationship: "" });
  const [bank, setBank] = useState({ account_name: "", bank_name: "", account_number: "", branch: "" });
  const [legal, setLegal] = useState({
    independent_contractor: false,
    food_safety: false,
    code_of_conduct: false,
  });
  const [watchedVideo, setWatchedVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Prefill guard so the effect only writes on first load — user edits after
  // that are respected instead of being overwritten by the prior submission.
  const prefilledRef = useRef(false);

  useEffect(() => {
    api.get("/driver/onboarding/config")
      .then((r) => setConfig(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Prefill prior submission data on rejection so the driver only has to
  // re-do the affected documents (spec + iteration_16 UX note).
  useEffect(() => {
    if (prefilledRef.current || !user) return;
    if (user.approval_status !== "rejected") return;
    prefilledRef.current = true;
    if (user.emergency_contact) setEmergency({
      name: user.emergency_contact.name || "",
      phone: user.emergency_contact.phone || "",
      relationship: user.emergency_contact.relationship || "",
    });
    if (user.bank_details) setBank({
      account_name: user.bank_details.account_name || "",
      bank_name: user.bank_details.bank_name || "",
      account_number: user.bank_details.account_number || "",
      branch: user.bank_details.branch || "",
    });
    if (user.legal_agreements) setLegal({
      independent_contractor: !!user.legal_agreements.independent_contractor,
      food_safety: !!user.legal_agreements.food_safety,
      code_of_conduct: !!user.legal_agreements.code_of_conduct,
    });
    if (user.training?.watched_video) setWatchedVideo(true);
  }, [user]);

  // Route away once the driver is in a state that no longer needs the form:
  // approved (dashboard) or pending_review (dashboard shows the "in review"
  // banner). Rejected drivers STAY on this page so they can re-upload.
  useEffect(() => {
    if (!user?.approval_status) return;
    if (user.approval_status === "approved" || user.approval_status === "pending_review") {
      navigate("/driver");
    }
  }, [user?.approval_status, navigate]);

  // Drivers whose application was rejected only need to fix the specific docs
  // the admin flagged. We shrink the checklist to just those keys so the UI
  // isn't visually overwhelming when they return to re-apply.
  const isRejected = user?.approval_status === "rejected";
  const docsToFix = user?.documents_to_reupload || [];
  const visibleDocs = isRejected && docsToFix.length > 0
    ? DOC_CATALOG.filter((d) => docsToFix.includes(d.key))
    : DOC_CATALOG;

  const handleUpload = async (docKey, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8 MB");
      return;
    }
    setUploading((u) => ({ ...u, [docKey]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/driver/upload-document?doc_key=${docKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((prev) => ({ ...prev, [docKey]: { storage_path: data.storage_path, size: data.size, filename: file.name } }));
      toast.success(`${docLabel(docKey)} uploaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading((u) => ({ ...u, [docKey]: false }));
    }
  };

  const allDocsUploaded = visibleDocs.every((d) => docs[d.key]?.storage_path);
  const allLegal = legal.independent_contractor && legal.food_safety && legal.code_of_conduct;
  const bankValid = bank.account_name && bank.bank_name && bank.account_number;
  const emergencyValid = emergency.name && emergency.phone;
  const canSubmit = allDocsUploaded && allLegal && watchedVideo && bankValid && emergencyValid && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        // Only send the docs we actually uploaded this session — backend
        // merges them on top of any prior submission.
        documents: Object.fromEntries(
          Object.entries(docs).map(([k, v]) => [k, v.storage_path]),
        ),
        emergency_contact: emergency,
        bank_details: bank,
        agreed_independent_contractor: legal.independent_contractor,
        agreed_food_safety: legal.food_safety,
        agreed_code_of_conduct: legal.code_of_conduct,
        watched_training_video: watchedVideo,
      };
      await api.post("/driver/onboarding", payload);
      toast.success(isRejected
        ? "Documents resubmitted — an admin will review them shortly."
        : "Application submitted — an admin will review your documents shortly.");
      await refreshUser?.();
      navigate("/driver");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-eatup-gray">
      <header className="bg-white border-b border-neutral-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <img src="/eats-gnd-logo.png" alt="" className="w-10 h-10 rounded-xl" />
          <div>
            <div className="font-display font-bold text-lg">Driver onboarding</div>
            <div className="text-xs text-neutral-500">Complete every section before you can go online.</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="driver-onboarding-page">
        {isRejected && (
          <div
            data-testid="rejection-banner"
            className="rounded-3xl bg-red-50 border border-red-200 p-5 sm:p-6"
          >
            <div className="flex items-start gap-3">
              <CircleAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-display font-bold text-red-700 text-lg">
                  Your application needs a fix
                </div>
                <div className="text-sm text-red-700/90 mt-1">
                  {docsToFix.length > 0
                    ? `Please re-upload the ${docsToFix.length} document${docsToFix.length === 1 ? "" : "s"} below and resubmit.`
                    : "Please review your submission and try again."}
                </div>
                {user?.approval_notes && (
                  <div
                    data-testid="rejection-notes"
                    className="mt-3 rounded-2xl bg-white border border-red-100 px-3 py-2.5"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-red-600 font-black">Reviewer notes</div>
                    <div className="text-sm text-eatup-charcoal mt-0.5 whitespace-pre-wrap">{user.approval_notes}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents section */}
        <Section
          testid="section-documents"
          icon={FileText}
          title={isRejected && docsToFix.length > 0 ? "Documents to re-upload" : "Documents"}
          subtitle={isRejected && docsToFix.length > 0
            ? "Only the documents flagged by the reviewer need to be re-uploaded."
            : "Upload a clear photo or PDF of each of the following. Max 8 MB per file."}
        >
          <div className="grid gap-3">
            {visibleDocs.map((d) => (
              <DocumentUploader
                key={d.key}
                docKey={d.key}
                label={d.label}
                hint={d.hint}
                doc={docs[d.key]}
                uploading={!!uploading[d.key]}
                onUpload={(file) => handleUpload(d.key, file)}
              />
            ))}
          </div>
        </Section>

        {/* Emergency contact */}
        <Section
          testid="section-emergency"
          icon={Phone}
          title="Emergency contact"
          subtitle="Who should we call if there's an incident on the road?"
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <InputField
              testid="emergency-name"
              label="Full name"
              value={emergency.name}
              onChange={(v) => setEmergency({ ...emergency, name: v })}
              placeholder="e.g. Kwesi Baptiste"
            />
            <InputField
              testid="emergency-phone"
              label="Phone number"
              value={emergency.phone}
              onChange={(v) => setEmergency({ ...emergency, phone: v })}
              placeholder="+1 473 555 1234"
            />
            <InputField
              testid="emergency-relationship"
              label="Relationship"
              value={emergency.relationship}
              onChange={(v) => setEmergency({ ...emergency, relationship: v })}
              placeholder="e.g. spouse, parent"
            />
          </div>
        </Section>

        {/* Legal */}
        <Section
          testid="section-legal"
          icon={ShieldCheck}
          title="Legal agreements"
          subtitle="You must accept all three to work as an Eats GND driver."
        >
          <div className="space-y-2.5">
            <CheckboxRow
              testid="legal-independent-contractor"
              checked={legal.independent_contractor}
              onChange={(v) => setLegal({ ...legal, independent_contractor: v })}
              title="Independent Contractor Agreement"
              hint="I understand I operate as an independent contractor, not an employee, and I agree to the terms."
            />
            <CheckboxRow
              testid="legal-food-safety"
              checked={legal.food_safety}
              onChange={(v) => setLegal({ ...legal, food_safety: v })}
              title="Food Safety Agreement"
              hint="I will maintain temperature control, cleanliness, and handle every order with care."
            />
            <CheckboxRow
              testid="legal-code-of-conduct"
              checked={legal.code_of_conduct}
              onChange={(v) => setLegal({ ...legal, code_of_conduct: v })}
              title="Driver Code of Conduct"
              hint="I will be respectful, punctual, honest and drive safely on every trip."
            />
          </div>
        </Section>

        {/* Onboarding training */}
        <Section
          testid="section-training"
          icon={Video}
          title="Onboarding training"
          subtitle="Watch the full training video, then confirm below."
        >
          <div className="rounded-2xl overflow-hidden bg-black aspect-video">
            {config.training_video_url ? (
              <iframe
                title="Eats GND driver training"
                src={config.training_video_url}
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                data-testid="training-video-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/60">
                <PlayCircle className="w-10 h-10" />
              </div>
            )}
          </div>
          <div className="mt-3">
            <CheckboxRow
              testid="training-watched"
              checked={watchedVideo}
              onChange={setWatchedVideo}
              title="I confirm I've watched the entire training video"
              hint="You must complete the video before submitting."
            />
          </div>
        </Section>

        {/* Bank details */}
        <Section
          testid="section-bank"
          icon={CreditCard}
          title="Bank account (for payouts)"
          subtitle="This is where your weekly earnings will be sent."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <InputField
              testid="bank-account-name"
              label="Account holder name"
              value={bank.account_name}
              onChange={(v) => setBank({ ...bank, account_name: v })}
              placeholder="e.g. Kwesi Baptiste"
            />
            <InputField
              testid="bank-name"
              label="Bank name"
              value={bank.bank_name}
              onChange={(v) => setBank({ ...bank, bank_name: v })}
              placeholder="e.g. Republic Bank"
            />
            <InputField
              testid="bank-account-number"
              label="Account number"
              value={bank.account_number}
              onChange={(v) => setBank({ ...bank, account_number: v })}
              placeholder="Digits only"
            />
            <InputField
              testid="bank-branch"
              label="Branch (optional)"
              value={bank.branch}
              onChange={(v) => setBank({ ...bank, branch: v })}
              placeholder="e.g. Grand Anse"
            />
          </div>
        </Section>

        {/* Submit */}
        <div className="bg-white border border-neutral-200 rounded-3xl p-6 sm:p-7">
          <div className="flex items-start gap-3">
            <CircleAlert className="w-5 h-5 text-eatup-green shrink-0 mt-0.5" />
            <p className="text-sm text-neutral-600">
              Once you submit, your account will be marked <span className="font-bold text-eatup-charcoal">Pending Approval</span>.
              An Eats GND admin will review your documents before you can go online.
            </p>
          </div>
          <button
            data-testid="onboarding-submit-btn"
            onClick={submit}
            disabled={!canSubmit}
            className="mt-4 w-full py-4 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white font-display font-black text-lg tracking-widest uppercase transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : <>Submit for approval</>}
          </button>
          {!canSubmit && (
            <div className="mt-3 text-[11px] text-neutral-500 text-center" data-testid="onboarding-progress-hint">
              {visibleDocs.filter((d) => docs[d.key]?.storage_path).length}/{visibleDocs.length} documents ·
              {" "}{allLegal ? "3/3" : `${Object.values(legal).filter(Boolean).length}/3`} agreements ·
              {" "}{watchedVideo ? "training done" : "training pending"} ·
              {" "}{bankValid ? "bank ✓" : "bank pending"} ·
              {" "}{emergencyValid ? "emergency ✓" : "emergency pending"}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function docLabel(key) {
  return DOC_CATALOG.find((d) => d.key === key)?.label || key;
}

function Section({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white border border-neutral-200 rounded-3xl p-6 sm:p-7">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-eatup-green/10 text-eatup-green flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display font-bold text-lg leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function DocumentUploader({ docKey, label, hint, doc, uploading, onUpload }) {
  const inputRef = useRef(null);
  const uploaded = !!doc?.storage_path;
  return (
    <div
      data-testid={`doc-row-${docKey}`}
      className={`rounded-2xl border p-3 flex items-center gap-3 transition ${
        uploaded ? "border-eatup-green/40 bg-eatup-green/5" : "border-neutral-200"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          uploaded ? "bg-eatup-green text-white" : "bg-neutral-100 text-neutral-500"
        }`}
      >
        {uploaded ? <Check className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{label}</div>
        <div className="text-xs text-neutral-500 truncate">
          {uploaded ? `Uploaded · ${doc.filename || docKey}` : hint}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_MIME}
        className="hidden"
        data-testid={`doc-input-${docKey}`}
        onChange={(e) => onUpload(e.target.files?.[0])}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`doc-upload-btn-${docKey}`}
        className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition shrink-0 disabled:opacity-60 ${
          uploaded
            ? "bg-white border border-eatup-green/40 text-eatup-green hover:bg-eatup-green/10"
            : "bg-eatup-charcoal text-white hover:bg-black"
        }`}
      >
        {uploading ? (
          <><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Uploading</>
        ) : uploaded ? (
          "Replace"
        ) : (
          <><Upload className="w-3 h-3 inline mr-1" /> Upload</>
        )}
      </button>
    </div>
  );
}

function CheckboxRow({ checked, onChange, title, hint, testid }) {
  return (
    <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 hover:border-eatup-green/40 p-3 cursor-pointer transition">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-1 w-4 h-4 accent-eatup-green shrink-0"
      />
      <div className="min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        {hint && <div className="text-xs text-neutral-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}

function InputField({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-1.5">{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-eatup-green/30 focus:bg-white focus:border-eatup-green/40 transition"
      />
    </label>
  );
}
